/**
 * Frontend half of the dashboard widget registry.
 *
 * Maps each widget key registered backend-side (see
 * shared/services/dashboard_widget_registry.py and
 * docs/platform/dashboard-widget-registry.md) to its lazily-loaded React
 * component. The home command center resolves the tenant's layout from
 * GET /api/v1/dashboard/layout and renders each placement through this map;
 * a key with no entry here is skipped (and logged) rather than crashing the
 * home page.
 *
 * Contributing module components are imported lazily so home does not pull
 * every module's dashboard code into its initial chunk.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { DashboardWidgetProps } from './widgetConfig';

export const DASHBOARD_WIDGET_COMPONENTS: Record<
  string,
  LazyExoticComponent<ComponentType<DashboardWidgetProps>>
> = {
  tasks_stats: lazy(() =>
    import('../../../modules/tasks/components/widgets/TasksStatsWidget').then(
      (m) => ({
        default: m.TasksStatsWidget
      })
    )
  ),
  tasks_due: lazy(() =>
    import('../../../modules/tasks/components/widgets/TasksDueWidget').then(
      (m) => ({
        default: m.TasksDueWidget
      })
    )
  ),
  tasks_overdue: lazy(() =>
    import('../../../modules/tasks/components/widgets/TasksOverdueWidget').then(
      (m) => ({
        default: m.TasksOverdueWidget
      })
    )
  ),
  tasks_by_status: lazy(() =>
    import('../../../modules/tasks/components/widgets/TasksByStatusWidget').then(
      (m) => ({
        default: m.TasksByStatusWidget
      })
    )
  ),
  tasks_active_sprints: lazy(() =>
    import('../../../modules/tasks/components/widgets/TasksActiveSprintsWidget').then(
      (m) => ({
        default: m.TasksActiveSprintsWidget
      })
    )
  ),
  tasks_workload: lazy(() =>
    import('../../../modules/tasks/components/widgets/TasksWorkloadWidget').then(
      (m) => ({
        default: m.TasksWorkloadWidget
      })
    )
  ),
  tasks_activity: lazy(() =>
    import('../../../modules/tasks/components/widgets/TasksActivityWidget').then(
      (m) => ({
        default: m.TasksActivityWidget
      })
    )
  ),
  pipeline_summary: lazy(() =>
    import('../../../modules/pipeline/components/widgets/PipelineSummaryWidget').then(
      (m) => ({
        default: m.PipelineSummaryWidget
      })
    )
  ),
  pipeline_win_rate: lazy(() =>
    import('../../../modules/pipeline/components/widgets/PipelineWinRateWidget').then(
      (m) => ({
        default: m.PipelineWinRateWidget
      })
    )
  ),
  pipeline_value_by_stage: lazy(() =>
    import('../../../modules/pipeline/components/widgets/PipelineValueByStageWidget').then(
      (m) => ({
        default: m.PipelineValueByStageWidget
      })
    )
  ),
  pipeline_recent_deals: lazy(() =>
    import('../../../modules/pipeline/components/widgets/RecentDealsWidget').then(
      (m) => ({
        default: m.RecentDealsWidget
      })
    )
  ),
  pipeline_activity: lazy(() =>
    import('../../../modules/pipeline/components/widgets/PipelineActivityWidget').then(
      (m) => ({
        default: m.PipelineActivityWidget
      })
    )
  ),
  pipeline_lead_summary: lazy(() =>
    import('../../../modules/pipeline/components/widgets/LeadSummaryWidget').then(
      (m) => ({
        default: m.LeadSummaryWidget
      })
    )
  ),
  pipeline_lead_conversion: lazy(() =>
    import('../../../modules/pipeline/components/widgets/LeadConversionWidget').then(
      (m) => ({
        default: m.LeadConversionWidget
      })
    )
  ),
  pipeline_leads_by_status: lazy(() =>
    import('../../../modules/pipeline/components/widgets/LeadsByStatusWidget').then(
      (m) => ({
        default: m.LeadsByStatusWidget
      })
    )
  ),
  pipeline_leads_by_source: lazy(() =>
    import('../../../modules/pipeline/components/widgets/LeadsBySourceWidget').then(
      (m) => ({
        default: m.LeadsBySourceWidget
      })
    )
  ),
  pipeline_won_lost: lazy(() =>
    import('../../../modules/pipeline/components/widgets/PipelineWonLostWidget').then(
      (m) => ({
        default: m.PipelineWonLostWidget
      })
    )
  ),
  pipeline_book_of_business: lazy(() =>
    import('../../../modules/pipeline/components/widgets/PipelineBookOfBusinessWidget').then(
      (m) => ({
        default: m.PipelineBookOfBusinessWidget
      })
    )
  ),
  ar_aging: lazy(() =>
    import('../../../modules/accounting/components/widgets/ArAgingWidget').then(
      (m) => ({
        default: m.ArAgingWidget
      })
    )
  ),
  accounting_balances: lazy(() =>
    import('../../../modules/accounting/components/widgets/AccountingBalancesWidget').then(
      (m) => ({
        default: m.AccountingBalancesWidget
      })
    )
  ),
  accounting_pnl: lazy(() =>
    import('../../../modules/accounting/components/widgets/AccountingPnlWidget').then(
      (m) => ({
        default: m.AccountingPnlWidget
      })
    )
  ),
  accounting_recent_journal_entries: lazy(() =>
    import('../../../modules/accounting/components/widgets/AccountingRecentJournalEntriesWidget').then(
      (m) => ({ default: m.AccountingRecentJournalEntriesWidget })
    )
  ),
  accounting_cash_position: lazy(() =>
    import('../../../modules/accounting/components/widgets/AccountingCashPositionWidget').then(
      (m) => ({ default: m.AccountingCashPositionWidget })
    )
  ),
  accounting_ap_due: lazy(() =>
    import('../../../modules/accounting/components/widgets/AccountingApDueWidget').then(
      (m) => ({
        default: m.AccountingApDueWidget
      })
    )
  ),
  accounting_overdue_invoices: lazy(() =>
    import('../../../modules/accounting/components/widgets/AccountingOverdueInvoicesWidget').then(
      (m) => ({ default: m.AccountingOverdueInvoicesWidget })
    )
  ),
  accounting_bills_awaiting_approval: lazy(() =>
    import('../../../modules/accounting/components/widgets/AccountingBillsAwaitingApprovalWidget').then(
      (m) => ({ default: m.AccountingBillsAwaitingApprovalWidget })
    )
  ),
  accounting_imports_awaiting_review: lazy(() =>
    import('../../../modules/accounting/components/widgets/AccountingImportsAwaitingReviewWidget').then(
      (m) => ({ default: m.AccountingImportsAwaitingReviewWidget })
    )
  ),
  accounting_unreconciled_bank_lines: lazy(() =>
    import('../../../modules/accounting/components/widgets/AccountingUnreconciledBankLinesWidget').then(
      (m) => ({ default: m.AccountingUnreconciledBankLinesWidget })
    )
  ),
  accounting_pnl_by_office: lazy(() =>
    import('../../../modules/accounting/components/widgets/AccountingPnlByOfficeWidget').then(
      (m) => ({ default: m.AccountingPnlByOfficeWidget })
    )
  ),
  accounting_period_close: lazy(() =>
    import('../../../modules/accounting/components/widgets/AccountingPeriodCloseWidget').then(
      (m) => ({ default: m.AccountingPeriodCloseWidget })
    )
  ),
  activity_feed: lazy(() =>
    import('../../../modules/home/components/widgets/ActivityFeedWidget').then(
      (m) => ({
        default: m.ActivityFeedWidget
      })
    )
  ),
  announcements_feed: lazy(() =>
    import('../../../modules/home/components/widgets/AnnouncementsFeedWidget').then(
      (m) => ({
        default: m.AnnouncementsFeedWidget
      })
    )
  ),
  luma_quick: lazy(() =>
    import('../../../modules/home/components/widgets/LumaQuickWidget').then(
      (m) => ({
        default: m.LumaQuickWidget
      })
    )
  ),
  sales_open_orders: lazy(() =>
    import('../../../modules/sales/components/widgets/SalesOpenOrdersWidget').then(
      (m) => ({
        default: m.SalesOpenOrdersWidget
      })
    )
  ),
  sales_revenue_by_month: lazy(() =>
    import('../../../modules/sales/components/widgets/SalesRevenueByMonthWidget').then(
      (m) => ({
        default: m.SalesRevenueByMonthWidget
      })
    )
  ),
  sales_recent_orders: lazy(() =>
    import('../../../modules/sales/components/widgets/SalesRecentOrdersWidget').then(
      (m) => ({
        default: m.SalesRecentOrdersWidget
      })
    )
  ),
  sales_backordered: lazy(() =>
    import('../../../modules/sales/components/widgets/SalesBackorderedWidget').then(
      (m) => ({
        default: m.SalesBackorderedWidget
      })
    )
  ),
  sales_quotes: lazy(() =>
    import('../../../modules/sales/components/widgets/SalesQuotesWidget').then(
      (m) => ({
        default: m.SalesQuotesWidget
      })
    )
  ),
  support_open_tickets: lazy(() =>
    import('../../../modules/support/components/widgets/SupportOpenTicketsWidget').then(
      (m) => ({
        default: m.SupportOpenTicketsWidget
      })
    )
  ),
  support_tickets_by_status: lazy(() =>
    import('../../../modules/support/components/widgets/SupportTicketsByStatusWidget').then(
      (m) => ({
        default: m.SupportTicketsByStatusWidget
      })
    )
  ),
  support_csat: lazy(() =>
    import('../../../modules/support/components/widgets/SupportCsatWidget').then(
      (m) => ({
        default: m.SupportCsatWidget
      })
    )
  ),
  support_recent_tickets: lazy(() =>
    import('../../../modules/support/components/widgets/SupportRecentTicketsWidget').then(
      (m) => ({
        default: m.SupportRecentTicketsWidget
      })
    )
  ),
  support_by_priority: lazy(() =>
    import('../../../modules/support/components/widgets/SupportTicketsByPriorityWidget').then(
      (m) => ({
        default: m.SupportTicketsByPriorityWidget
      })
    )
  ),
  support_queue_health: lazy(() =>
    import('../../../modules/support/components/widgets/SupportQueueHealthWidget').then(
      (m) => ({
        default: m.SupportQueueHealthWidget
      })
    )
  ),
  forms_bpm_content: lazy(() =>
    import('../../../modules/forms_bpm/components/widgets/FormsBpmContentWidget').then(
      (m) => ({
        default: m.FormsBpmContentWidget
      })
    )
  ),
  forms_bpm_workflows: lazy(() =>
    import('../../../modules/forms_bpm/components/widgets/FormsBpmWorkflowsWidget').then(
      (m) => ({
        default: m.FormsBpmWorkflowsWidget
      })
    )
  ),
  forms_bpm_submissions_by_status: lazy(() =>
    import('../../../modules/forms_bpm/components/widgets/FormsBpmSubmissionsByStatusWidget').then(
      (m) => ({
        default: m.FormsBpmSubmissionsByStatusWidget
      })
    )
  ),
  forms_bpm_recent_submissions: lazy(() =>
    import('../../../modules/forms_bpm/components/widgets/FormsBpmRecentSubmissionsWidget').then(
      (m) => ({
        default: m.FormsBpmRecentSubmissionsWidget
      })
    )
  ),
  forms_bpm_awaiting_review: lazy(() =>
    import('../../../modules/forms_bpm/components/widgets/FormsBpmAwaitingReviewWidget').then(
      (m) => ({
        default: m.FormsBpmAwaitingReviewWidget
      })
    )
  ),
  forms_bpm_approval_rate: lazy(() =>
    import('../../../modules/forms_bpm/components/widgets/FormsBpmApprovalRateWidget').then(
      (m) => ({
        default: m.FormsBpmApprovalRateWidget
      })
    )
  ),
  inventory_stock_overview: lazy(() =>
    import('../../../modules/inventory/components/widgets/InventoryStockOverviewWidget').then(
      (m) => ({ default: m.InventoryStockOverviewWidget })
    )
  ),
  inventory_stock_by_location: lazy(() =>
    import('../../../modules/inventory/components/widgets/InventoryStockByLocationWidget').then(
      (m) => ({ default: m.InventoryStockByLocationWidget })
    )
  ),
  inventory_recent_movements: lazy(() =>
    import('../../../modules/inventory/components/widgets/InventoryRecentMovementsWidget').then(
      (m) => ({ default: m.InventoryRecentMovementsWidget })
    )
  ),
  inventory_stock_value: lazy(() =>
    import('../../../modules/inventory/components/widgets/InventoryStockValueWidget').then(
      (m) => ({
        default: m.InventoryStockValueWidget
      })
    )
  ),
  inventory_posting_exceptions: lazy(() =>
    import('../../../modules/inventory/components/widgets/InventoryPostingExceptionsWidget').then(
      (m) => ({
        default: m.InventoryPostingExceptionsWidget
      })
    )
  ),
  procurement_spend_overview: lazy(() =>
    import('../../../modules/procurement/components/widgets/ProcurementSpendOverviewWidget').then(
      (m) => ({ default: m.ProcurementSpendOverviewWidget })
    )
  ),
  procurement_spend_by_vendor: lazy(() =>
    import('../../../modules/procurement/components/widgets/ProcurementSpendByVendorWidget').then(
      (m) => ({ default: m.ProcurementSpendByVendorWidget })
    )
  ),
  procurement_recent_receipts: lazy(() =>
    import('../../../modules/procurement/components/widgets/ProcurementRecentReceiptsWidget').then(
      (m) => ({ default: m.ProcurementRecentReceiptsWidget })
    )
  ),
  procurement_monthly_spend: lazy(() =>
    import('../../../modules/procurement/components/widgets/ProcurementMonthlySpendWidget').then(
      (m) => ({
        default: m.ProcurementMonthlySpendWidget
      })
    )
  ),
  procurement_recent_pos: lazy(() =>
    import('../../../modules/procurement/components/widgets/ProcurementRecentPosWidget').then(
      (m) => ({
        default: m.ProcurementRecentPosWidget
      })
    )
  ),
  ai_agents_activity: lazy(() =>
    import('../../../modules/ai_agents/components/widgets/AiAgentsActivityWidget').then(
      (m) => ({
        default: m.AiAgentsActivityWidget
      })
    )
  ),
  ai_agents_runs_by_status: lazy(() =>
    import('../../../modules/ai_agents/components/widgets/AiAgentsRunsByStatusWidget').then(
      (m) => ({ default: m.AiAgentsRunsByStatusWidget })
    )
  ),
  ai_agents_recent_runs: lazy(() =>
    import('../../../modules/ai_agents/components/widgets/AiAgentsRecentRunsWidget').then(
      (m) => ({
        default: m.AiAgentsRecentRunsWidget
      })
    )
  ),
  ai_agents_pending_approvals: lazy(() =>
    import('../../../modules/ai_agents/components/widgets/AiAgentsPendingApprovalsWidget').then(
      (m) => ({ default: m.AiAgentsPendingApprovalsWidget })
    )
  ),
  ai_agents_token_usage: lazy(() =>
    import('../../../modules/ai_agents/components/widgets/AiAgentsTokenUsageWidget').then(
      (m) => ({
        default: m.AiAgentsTokenUsageWidget
      })
    )
  ),
  ai_agents_error_rate: lazy(() =>
    import('../../../modules/ai_agents/components/widgets/AiAgentsErrorRateWidget').then(
      (m) => ({
        default: m.AiAgentsErrorRateWidget
      })
    )
  ),
  ai_agents_roster: lazy(() =>
    import('../../../modules/ai_agents/components/widgets/AiAgentsRosterWidget').then(
      (m) => ({
        default: m.AiAgentsRosterWidget
      })
    )
  ),
  ai_agents_run_volume: lazy(() =>
    import('../../../modules/ai_agents/components/widgets/AiAgentsRunVolumeWidget').then(
      (m) => ({
        default: m.AiAgentsRunVolumeWidget
      })
    )
  ),
  shipments_in_transit: lazy(() =>
    import('../../../modules/shipments/components/widgets/ShipmentsInTransitWidget').then(
      (m) => ({
        default: m.ShipmentsInTransitWidget
      })
    )
  ),
  shipments_in_transit_map: lazy(() =>
    import('../../../modules/shipments/components/widgets/ShipmentsInTransitMapWidget').then(
      (m) => ({
        default: m.ShipmentsInTransitMapWidget
      })
    )
  ),
  shipments_by_status: lazy(() =>
    import('../../../modules/shipments/components/widgets/ShipmentsByStatusWidget').then(
      (m) => ({
        default: m.ShipmentsByStatusWidget
      })
    )
  ),
  shipments_recent_tracking: lazy(() =>
    import('../../../modules/shipments/components/widgets/ShipmentsRecentTrackingWidget').then(
      (m) => ({ default: m.ShipmentsRecentTrackingWidget })
    )
  ),
  shipments_forwarder_kpis: lazy(() =>
    import('../../../modules/shipments/components/widgets/ForwarderKpisWidget').then(
      (m) => ({
        default: m.ForwarderKpisWidget
      })
    )
  ),
  shipments_files_in_motion: lazy(() =>
    import('../../../modules/shipments/components/widgets/FilesInMotionWidget').then(
      (m) => ({
        default: m.FilesInMotionWidget
      })
    )
  ),
  shipments_exception_triage: lazy(() =>
    import('../../../modules/shipments/components/widgets/ExceptionTriageWidget').then(
      (m) => ({
        default: m.ExceptionTriageWidget
      })
    )
  ),
  shipments_luma_briefing: lazy(() =>
    import('../../../modules/shipments/components/widgets/LumaBriefingWidget').then(
      (m) => ({
        default: m.LumaBriefingWidget
      })
    )
  ),
  shipments_ai_crew_feed: lazy(() =>
    import('../../../modules/shipments/components/widgets/AiCrewFeedWidget').then(
      (m) => ({
        default: m.AiCrewFeedWidget
      })
    )
  ),
  shipments_network_board: lazy(() =>
    import('../../../modules/shipments/components/widgets/NetworkBoardWidget').then(
      (m) => ({
        default: m.NetworkBoardWidget
      })
    )
  ),
  shipments_missing_docs: lazy(() =>
    import('../../../modules/shipments/components/widgets/MissingDocsWidget').then(
      (m) => ({
        default: m.MissingDocsWidget
      })
    )
  ),
  shipments_agent_balances: lazy(() =>
    import('../../../modules/shipments/components/widgets/AgentBalancesWidget').then(
      (m) => ({
        default: m.AgentBalancesWidget
      })
    )
  ),
  shipments_fx_exposure: lazy(() =>
    import('../../../modules/shipments/components/widgets/FxExposureWidget').then(
      (m) => ({
        default: m.FxExposureWidget
      })
    )
  ),
  shipments_eta_changes: lazy(() =>
    import('../../../modules/shipments/components/widgets/EtaChangeDiffWidget').then(
      (m) => ({
        default: m.EtaChangeDiffWidget
      })
    )
  ),
  shipments_silent_shipments: lazy(() =>
    import('../../../modules/shipments/components/widgets/SilentShipmentsWidget').then(
      (m) => ({
        default: m.SilentShipmentsWidget
      })
    )
  ),
  shipments_rollover_watch: lazy(() =>
    import('../../../modules/shipments/components/widgets/RolloverWatchWidget').then(
      (m) => ({
        default: m.RolloverWatchWidget
      })
    )
  ),
  shipments_carrier_scorecard: lazy(() =>
    import('../../../modules/shipments/components/widgets/CarrierScorecardWidget').then(
      (m) => ({
        default: m.CarrierScorecardWidget
      })
    )
  ),
  recruiting_open_jobs: lazy(() =>
    import('../../../modules/recruiting/components/widgets/RecruitingOpenJobsWidget').then(
      (m) => ({
        default: m.RecruitingOpenJobsWidget
      })
    )
  ),
  recruiting_candidate_funnel: lazy(() =>
    import('../../../modules/recruiting/components/widgets/RecruitingCandidateFunnelWidget').then(
      (m) => ({
        default: m.RecruitingCandidateFunnelWidget
      })
    )
  ),
  recruiting_upcoming_interviews: lazy(() =>
    import('../../../modules/recruiting/components/widgets/RecruitingUpcomingInterviewsWidget').then(
      (m) => ({
        default: m.RecruitingUpcomingInterviewsWidget
      })
    )
  ),
  recruiting_hiring_activity: lazy(() =>
    import('../../../modules/recruiting/components/widgets/RecruitingHiringActivityWidget').then(
      (m) => ({
        default: m.RecruitingHiringActivityWidget
      })
    )
  ),
  recruiting_interview_load: lazy(() =>
    import('../../../modules/recruiting/components/widgets/RecruitingInterviewLoadWidget').then(
      (m) => ({
        default: m.RecruitingInterviewLoadWidget
      })
    )
  ),
  people_headcount: lazy(() =>
    import('../../../modules/people/components/widgets/PeopleHeadcountWidget').then(
      (m) => ({
        default: m.PeopleHeadcountWidget
      })
    )
  ),
  people_headcount_by_department: lazy(() =>
    import('../../../modules/people/components/widgets/PeopleHeadcountByDepartmentWidget').then(
      (m) => ({
        default: m.PeopleHeadcountByDepartmentWidget
      })
    )
  ),
  people_headcount_trend: lazy(() =>
    import('../../../modules/people/components/widgets/PeopleHeadcountTrendWidget').then(
      (m) => ({
        default: m.PeopleHeadcountTrendWidget
      })
    )
  ),
  people_headcount_by_office: lazy(() =>
    import('../../../modules/people/components/widgets/PeopleHeadcountByOfficeWidget').then(
      (m) => ({
        default: m.PeopleHeadcountByOfficeWidget
      })
    )
  ),
  people_terminations_by_reason: lazy(() =>
    import('../../../modules/people/components/widgets/PeopleTerminationsByReasonWidget').then(
      (m) => ({
        default: m.PeopleTerminationsByReasonWidget
      })
    )
  ),
  people_upcoming_time_off: lazy(() =>
    import('../../../modules/people/components/widgets/PeopleUpcomingTimeOffWidget').then(
      (m) => ({
        default: m.PeopleUpcomingTimeOffWidget
      })
    )
  ),
  people_expiring_certifications: lazy(() =>
    import('../../../modules/people/components/widgets/PeopleExpiringCertificationsWidget').then(
      (m) => ({
        default: m.PeopleExpiringCertificationsWidget
      })
    )
  ),
  people_reviews_pending: lazy(() =>
    import('../../../modules/people/components/widgets/PeopleReviewsPendingWidget').then(
      (m) => ({
        default: m.PeopleReviewsPendingWidget
      })
    )
  ),
  people_review_cycle_progress: lazy(() =>
    import('../../../modules/people/components/widgets/PeopleReviewCycleProgressWidget').then(
      (m) => ({
        default: m.PeopleReviewCycleProgressWidget
      })
    )
  ),
  people_goal_progress: lazy(() =>
    import('../../../modules/people/components/widgets/PeopleGoalProgressWidget').then(
      (m) => ({
        default: m.PeopleGoalProgressWidget
      })
    )
  ),
  contracts_active: lazy(() =>
    import('../../../modules/contracts/components/widgets/ContractsActiveWidget').then(
      (m) => ({
        default: m.ContractsActiveWidget
      })
    )
  ),
  contracts_by_status: lazy(() =>
    import('../../../modules/contracts/components/widgets/ContractsByStatusWidget').then(
      (m) => ({
        default: m.ContractsByStatusWidget
      })
    )
  ),
  contracts_upcoming_obligations: lazy(() =>
    import('../../../modules/contracts/components/widgets/ContractsUpcomingObligationsWidget').then(
      (m) => ({
        default: m.ContractsUpcomingObligationsWidget
      })
    )
  ),
  contracts_created_trend: lazy(() =>
    import('../../../modules/contracts/components/widgets/ContractsCreatedTrendWidget').then(
      (m) => ({
        default: m.ContractsCreatedTrendWidget
      })
    )
  ),
  contracts_by_type: lazy(() =>
    import('../../../modules/contracts/components/widgets/ContractsByTypeWidget').then(
      (m) => ({
        default: m.ContractsByTypeWidget
      })
    )
  ),
  marketing_subscribers: lazy(() =>
    import('../../../modules/marketing/components/widgets/MarketingSubscribersWidget').then(
      (m) => ({ default: m.MarketingSubscribersWidget })
    )
  ),
  marketing_campaign_performance: lazy(() =>
    import('../../../modules/marketing/components/widgets/MarketingCampaignPerformanceWidget').then(
      (m) => ({ default: m.MarketingCampaignPerformanceWidget })
    )
  ),
  marketing_recent_sends: lazy(() =>
    import('../../../modules/marketing/components/widgets/MarketingRecentSendsWidget').then(
      (m) => ({ default: m.MarketingRecentSendsWidget })
    )
  ),
  marketing_delivery: lazy(() =>
    import('../../../modules/marketing/components/widgets/MarketingDeliveryWidget').then(
      (m) => ({
        default: m.MarketingDeliveryWidget
      })
    )
  ),
  marketing_top_sends: lazy(() =>
    import('../../../modules/marketing/components/widgets/MarketingTopSendsWidget').then(
      (m) => ({
        default: m.MarketingTopSendsWidget
      })
    )
  ),
  products_overview: lazy(() =>
    import('../../../modules/master_data/products/components/widgets/ProductsOverviewWidget').then(
      (m) => ({
        default: m.ProductsOverviewWidget
      })
    )
  ),
  products_by_category: lazy(() =>
    import('../../../modules/master_data/products/components/widgets/ProductsByCategoryWidget').then(
      (m) => ({
        default: m.ProductsByCategoryWidget
      })
    )
  ),
  products_recent: lazy(() =>
    import('../../../modules/master_data/products/components/widgets/ProductsRecentWidget').then(
      (m) => ({
        default: m.ProductsRecentWidget
      })
    )
  ),
  products_catalog_completeness: lazy(() =>
    import('../../../modules/master_data/products/components/widgets/ProductsCatalogCompletenessWidget').then(
      (m) => ({
        default: m.ProductsCatalogCompletenessWidget
      })
    )
  ),
  sf_work_item_pipeline: lazy(() =>
    import('../../../modules/software_factory/components/widgets/SfWorkItemPipelineWidget').then(
      (m) => ({ default: m.SfWorkItemPipelineWidget })
    )
  ),
  sf_deploy_debt: lazy(() =>
    import('../../../modules/software_factory/components/widgets/SfDeployDebtWidget').then(
      (m) => ({ default: m.SfDeployDebtWidget })
    )
  ),
  sf_active_sprint: lazy(() =>
    import('../../../modules/software_factory/components/widgets/SfActiveSprintWidget').then(
      (m) => ({ default: m.SfActiveSprintWidget })
    )
  ),
  sf_module_maturity: lazy(() =>
    import('../../../modules/software_factory/components/widgets/SfModuleMaturityWidget').then(
      (m) => ({ default: m.SfModuleMaturityWidget })
    )
  ),
  sf_exceptions: lazy(() =>
    import('../../../modules/software_factory/components/widgets/SfExceptionsWidget').then(
      (m) => ({ default: m.SfExceptionsWidget })
    )
  ),
  sf_recent_changelog: lazy(() =>
    import('../../../modules/software_factory/components/widgets/SfRecentChangelogWidget').then(
      (m) => ({ default: m.SfRecentChangelogWidget })
    )
  ),
  sf_items_by_type: lazy(() =>
    import('../../../modules/software_factory/components/widgets/SfItemsByTypeWidget').then(
      (m) => ({
        default: m.SfItemsByTypeWidget
      })
    )
  ),
  sf_items_by_status: lazy(() =>
    import('../../../modules/software_factory/components/widgets/SfItemsByStatusWidget').then(
      (m) => ({
        default: m.SfItemsByStatusWidget
      })
    )
  )
};
